import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  Typography,
  Button,
  Alert,
  Snackbar,
  Divider,
  CircularProgress
} from '@mui/material';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import MarinaAdminLayout from '../../layouts/MarinaAdminLayout';
import { useAuth } from '../../contexts/AuthContext';
import { getRoleById } from '../../firebase/services/roleService';
import { Role } from '../../types/rbac';

interface Policies {
  refundPolicy: string;
  cancellationPolicy: string;
  refundTimeLimit: number; // in hours
  cancellationTimeLimit: number; // in hours
  refundPercentage: number; // percentage of refund
  cancellationFeePercentage: number; // percentage of cancellation fee
  updatedAt: Date;
}

const POLICIES_DOC_ID = 'policies';

const PoliciesPage: React.FC = () => {
  const { userProfile } = useAuth();
  const [policies, setPolicies] = useState<Policies>({
    refundPolicy: '',
    cancellationPolicy: '',
    refundTimeLimit: 24,
    cancellationTimeLimit: 24,
    refundPercentage: 100,
    cancellationFeePercentage: 0,
    updatedAt: new Date()
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [roleName, setRoleName] = React.useState<string | null>(null);
  const [roleLoading, setRoleLoading] = React.useState<boolean>(true);

  useEffect(() => {
    fetchPolicies();
  }, []);

  React.useEffect(() => {
    const fetchRole = async () => {
      if (userProfile && userProfile.roleId) {
        setRoleLoading(true);
        const roleDoc = await getRoleById(userProfile.roleId);
        setRoleName(roleDoc?.name || null);
        setRoleLoading(false);
      } else {
        setRoleName(null);
        setRoleLoading(false);
      }
    };
    fetchRole();
  }, [userProfile]);

  const fetchPolicies = async () => {
    try {
      const docRef = doc(db, 'settings', POLICIES_DOC_ID);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        setPolicies(docSnap.data() as Policies);
      }
    } catch (err) {
      setError('Failed to load policies');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handlePolicyChange = (field: keyof Policies, value: string | number) => {
    setPolicies(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const docRef = doc(db, 'settings', POLICIES_DOC_ID);
      await setDoc(docRef, {
        ...policies,
        updatedAt: new Date()
      });
      setSuccess('Policies saved successfully');
    } catch (err) {
      setError('Failed to save policies');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (roleLoading) {
    return (
      <MarinaAdminLayout>
        <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </MarinaAdminLayout>
    );
  }

  if (!userProfile || roleName !== 'admin') {
    return (
      <MarinaAdminLayout>
        <Box sx={{ p: 3 }}>
          <Alert severity="error">
            You don't have permission to access this page
          </Alert>
        </Box>
      </MarinaAdminLayout>
    );
  }

  if (loading) {
    return (
      <MarinaAdminLayout>
        <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </MarinaAdminLayout>
    );
  }

  return (
    <MarinaAdminLayout>
      <Box sx={{ p: 3 }}>
        {error && (
          <Alert 
            severity="error" 
            onClose={() => setError(null)}
            sx={{ mb: 2 }}
          >
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert 
            severity="success"
            onClose={() => setSuccess(null)}
            sx={{ mb: 2 }}
          >
            {success}
          </Alert>
        )}
      
        <Typography variant="h4" gutterBottom>
          Marina Policies
        </Typography>

        <Grid container spacing={3}>
          {/* Refund Policy */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Refund Policy
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="Refund Policy Description"
                      value={policies.refundPolicy}
                      onChange={(e) => handlePolicyChange('refundPolicy', e.target.value)}
                      placeholder="Enter your refund policy details here..."
                      helperText="Describe your refund policy, including conditions and exceptions."
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Refund Time Limit (hours)"
                      value={policies.refundTimeLimit}
                      onChange={(e) => handlePolicyChange('refundTimeLimit', Number(e.target.value))}
                      InputProps={{ inputProps: { min: 0 } }}
                      helperText="Hours before reservation start time"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Refund Percentage"
                      value={policies.refundPercentage}
                      onChange={(e) => handlePolicyChange('refundPercentage', Number(e.target.value))}
                      InputProps={{ inputProps: { min: 0, max: 100 } }}
                      helperText="Percentage of total amount to refund"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Cancellation Policy */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Cancellation Policy
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="Cancellation Policy Description"
                      value={policies.cancellationPolicy}
                      onChange={(e) => handlePolicyChange('cancellationPolicy', e.target.value)}
                      placeholder="Enter your cancellation policy details here..."
                      helperText="Describe your cancellation policy, including conditions and exceptions."
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Cancellation Time Limit (hours)"
                      value={policies.cancellationTimeLimit}
                      onChange={(e) => handlePolicyChange('cancellationTimeLimit', Number(e.target.value))}
                      InputProps={{ inputProps: { min: 0 } }}
                      helperText="Hours before reservation start time"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Cancellation Fee Percentage"
                      value={policies.cancellationFeePercentage}
                      onChange={(e) => handlePolicyChange('cancellationFeePercentage', Number(e.target.value))}
                      InputProps={{ inputProps: { min: 0, max: 100 } }}
                      helperText="Percentage of total amount as cancellation fee"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Save Button */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={loading}
              >
                Save Policies
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </MarinaAdminLayout>
  );
};

export default PoliciesPage; 